import React from 'react';
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useLocation, useParams } from 'react-router-dom';

import { Header } from '@ledium/ui-header';
import { Login } from '@ledium/ui-login';
import { Alert } from '@mui/material';

import { useColorMode } from './colorMode';
import { PageErrorBoundary } from './components/PageErrorBoundary';
import { ZoneSelector } from './components/ZoneSelector';
import { RouteConfig, routeConfig } from './config/routeConfig';
import { UxProvider } from './context/UxContext';
import usePageConfig from './hooks/usePageConfig';
import { useGlobalStore } from './store/globalStore/context/globalContext';
import { PageStoreProvider } from './store/pageStore/context/pageContext';
import i18n from './utils/i18n';

type RouteParams = Record<string, string | undefined> & {
    pageType?: string;
    itemId?: string;
};

function getZonePath(path: string): string {
    const separatorIdx = path.indexOf('/:');
    const base = separatorIdx === -1 ? path : path.slice(0, separatorIdx);
    return base === '/' ? '/zone/:zoneId' : `${base}/zone/:zoneId`;
}

function toV6Path(path: string): string {
    return path.replace(/\([^)]+\)/g, '');
}

const HIDE_ZONE_SELECTOR_PATHS = ['/recipes', '/light-recipes', '/settings'];

function AppLayout() {
    const { pathname } = useLocation();
    const { colorMode, toggleColorMode } = useColorMode();
    const {
        globalState: { socketConnected }
    } = useGlobalStore();
    const showZoneSelector = !HIDE_ZONE_SELECTOR_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));

    return (
        <UxProvider>
            <Header colorMode={colorMode} onThemeToggle={toggleColorMode}>
                {showZoneSelector && <ZoneSelector />}
                {!socketConnected && (
                    <Alert severity="error" sx={{ borderRadius: 0 }}>
                        {i18n('socket_disconnected') as string}
                    </Alert>
                )}
                <Outlet />
            </Header>
        </UxProvider>
    );
}

function LoginRoute() {
    const {
        globalState: { userData }
    } = useGlobalStore();
    return userData.auth ? <Navigate to="/" replace /> : <Login />;
}

interface PrivateRouteElementProps {
    item: RouteConfig;
}

function PrivateRouteElement({ item }: PrivateRouteElementProps) {
    const { pageType, itemId } = useParams<RouteParams>();
    const { pathname } = useLocation();
    const {
        globalState: { userData }
    } = useGlobalStore();
    usePageConfig(item.pageName);

    if (!userData.auth) {
        return <Navigate to="/login" replace />;
    }

    if (pageType === 'edit' && itemId && userData.admin !== true) {
        return <Navigate to={pathname.replace('/edit/', '/view/')} replace />;
    }

    const isValidContext =
        (pageType === 'view' && !!itemId) || (pageType === 'edit' && !!itemId && userData.admin === true) || (pageType === 'new' && userData.admin === true);

    if (item.MenuComponent && !isValidContext) {
        return <item.MenuComponent />;
    }
    return (
        <PageErrorBoundary>
            <item.Component />
        </PageErrorBoundary>
    );
}

const routeChildren = [
    { path: '/login', element: <LoginRoute /> },
    ...routeConfig.flatMap((item) => {
        const routes = [];
        if (item.hasZoneRoute) {
            routes.push({
                path: getZonePath(item.path),
                element: (
                    <PageStoreProvider key={`${item.pageName}-zone`}>
                        <PrivateRouteElement item={item} />
                    </PageStoreProvider>
                )
            });
        }
        routes.push({
            path: toV6Path(item.path),
            element: (
                <PageStoreProvider key={`${item.pageName}-base`}>
                    <PrivateRouteElement item={item} />
                </PageStoreProvider>
            )
        });
        return routes;
    })
];

type RouterFactory = typeof createBrowserRouter;

export function createLediumRouter(factory: RouterFactory = createBrowserRouter) {
    return factory([{ element: <AppLayout />, children: routeChildren }], {
        future: { v7_relativeSplatPath: true }
    });
}

const router = createLediumRouter();

const LediumRouter = () => <RouterProvider router={router} future={{ v7_startTransition: true }} />;

export default LediumRouter;
